import { CloseOutlined } from '@ant-design/icons'
import { Vertical } from '@gravitate-js/excalibrr'
import { Drawer, Empty, Spin, theme } from 'antd'
import { useMemo } from 'react'
import { HistoryEntryCard } from './HistoryEntryCard'
import { type DocumentAudit, buildHistoryEntries } from './historyEntries'

export interface HistoryDrawerProps {
  open: boolean
  onClose: () => void
  /** Audit records for one document, as returned by a `/history` endpoint. */
  history: DocumentAudit[] | undefined
  /**
   * The document as it stands now. Needed to describe the newest revision,
   * which has no later audit record to compare against.
   */
  current?: Record<string, unknown>
  /**
   * Set when `current` is the full stored document rather than an API
   * projection of it — see BuildHistoryOptions.
   */
  currentIsFullDocument?: boolean
  loading?: boolean
  title?: string
  width?: number
  /** Overrides DEFAULT_IGNORED_KEYS. */
  ignoreKeys?: string[]
  emptyDescription?: string
  /**
   * Enables a Restore action on each entry. Called with the audit to restore
   * in order to reach that entry's resulting state — see HistoryEntry.
   */
  onRestore?: (auditId: string) => void
  /** _id of the audit currently being restored, if any. */
  restoringAuditId?: string | null
}

export function HistoryDrawer({
  open,
  onClose,
  history,
  current,
  currentIsFullDocument,
  loading,
  title = 'History',
  width = 680,
  ignoreKeys,
  emptyDescription = 'No history recorded for this record',
  onRestore,
  restoringAuditId,
}: HistoryDrawerProps) {
  const { token } = theme.useToken()

  const entries = useMemo(
    () =>
      buildHistoryEntries(history ?? [], current, {
        ignore: ignoreKeys,
        currentIsFullDocument,
      }),
    [history, current, ignoreKeys, currentIsFullDocument],
  )

  return (
    <Drawer
      title={title}
      placement='right'
      width={width}
      open={open}
      onClose={onClose}
      closeIcon={<CloseOutlined />}
      styles={{ body: { padding: 16 } }}
    >
      {loading ? (
        <Vertical
          className='h-full'
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
          }}
        >
          <Spin />
          <span style={{ marginTop: 12, color: token.colorTextSecondary }}>
            Loading history...
          </span>
        </Vertical>
      ) : entries.length === 0 ? (
        <Empty description={emptyDescription} />
      ) : (
        <div style={{ position: 'relative' }}>
          {/* Continuous timeline track */}
          {entries.length > 1 && (
            <div
              style={{
                position: 'absolute',
                left: 5, // center of 12px dot
                top: 24, // align with first dot (marginTop 18 + half dot 6)
                bottom: 24,
                width: 2,
                backgroundColor: token.colorBorderSecondary,
                zIndex: 0,
              }}
            />
          )}
          <Vertical gap={12}>
            {entries.map((entry) => (
              <HistoryEntryCard
                key={entry.audit._id ?? entry.audit.updated_on}
                entry={entry}
                onRestore={onRestore}
                restoring={
                  !!entry.restoreAuditId &&
                  entry.restoreAuditId === restoringAuditId
                }
              />
            ))}
          </Vertical>
        </div>
      )}
    </Drawer>
  )
}
